import { useState, useMemo, useEffect } from "react";
import { Plan, BillingPeriod } from "@/types/plans";
import { PricingHeroCard } from "./PricingHeroCard";
import { AlternativePlanCard } from "./AlternativePlanCard";
import { SocialProofBar } from "./SocialProofBar";
import { PLAN_DIFFERENTIALS } from "@/constants/planDifferentials";
import { useSafeAdaptiveViewport } from "@/contexts/AdaptiveViewportContext";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { useSocialProofStats } from "@/hooks/useSocialProofStats";

interface PlanSelectionGridProps {
  plans: Plan[];
  currentPlan: string;
  userRole?: string;
  onSelectionChange: (selection: { planId: string; period: BillingPeriod } | null) => void;
  initialSelection?: { planId: string; period: BillingPeriod } | null;
}

interface SelectedOption {
  planId: string;
  period: BillingPeriod;
}

export const PlanSelectionGrid = ({
  plans,
  currentPlan,
  userRole,
  onSelectionChange,
  initialSelection,
}: PlanSelectionGridProps) => {
  // 1. Estados básicos - Pré-seleciona quarterly (trimestral) como padrão
  const [selectedOption, setSelectedOption] = useState<SelectedOption | null>(
    initialSelection || null
  );
  const [isOtherPlansOpen, setIsOtherPlansOpen] = useState(false);
  const [previousPlan, setPreviousPlan] = useState<string | null>(() => {
    // Inicializar com o plano correto baseado no initialSelection
    if (initialSelection?.planId?.includes('standard')) {
      return 'standard';
    }
    return 'premium';
  });
  const { densityMode } = useSafeAdaptiveViewport();
  
  // Buscar dados dinâmicos do social proof
  const { data: socialProofStats, isLoading: socialProofLoading } = useSocialProofStats();
  
  // 2. Separate plans by type (useMemo PRIMEIRO)
  const premiumPlan = useMemo(() => 
    plans.find(p => p.id.includes('premium')), 
    [plans]
  );
  
  const standardPlan = useMemo(() => 
    plans.find(p => p.id.includes('standard')), 
    [plans]
  );
  
  const freePlan = useMemo(() => 
    plans.find(p => p.id.includes('free')), 
    [plans]
  );

  // 3. Estado para controlar qual plano está na posição Hero (DEPOIS dos useMemo)
  const [heroPlanId, setHeroPlanId] = useState<string>(
    initialSelection?.planId || premiumPlan?.id || ''
  );

  // 4. Detectar se temos plano standard (sponsors têm 3 planos, sponsoreds têm 2)
  const hasStandardPlan = !!standardPlan;

  // 5. Determine qual plano está em cada posição (DEPOIS de todos os estados)
  // Para sponsoreds (sem standard): premium é sempre hero, não há alternativePlan pago
  const heroPlan = hasStandardPlan 
    ? (heroPlanId === premiumPlan?.id ? premiumPlan : standardPlan)
    : premiumPlan;
  const alternativePlan = hasStandardPlan 
    ? (heroPlanId === premiumPlan?.id ? standardPlan : premiumPlan)
    : null; // Sponsoreds não têm plano alternativo pago
  
  // 6. Tier determina estilo (não posição)
  const heroPlanTier = heroPlan?.id.includes('premium') ? 'premium' as const : 'standard' as const;
  const alternativePlanTier = alternativePlan?.id.includes('premium') ? 'premium' as const : 'standard' as const;
  
  // 7. Detectar direção da animação (só relevante quando há standard plan)
  const direction = hasStandardPlan 
    ? (previousPlan === premiumPlan?.id && heroPlanId === standardPlan?.id ? 1 : 
       previousPlan === standardPlan?.id && heroPlanId === premiumPlan?.id ? -1 : 0)
    : 0;

  // 8. Sincronizar com initialSelection quando mudar
  useEffect(() => {
    if (initialSelection) {
      setSelectedOption(initialSelection);
      if (initialSelection.planId !== freePlan?.id && initialSelection.planId) {
        setHeroPlanId(initialSelection.planId);
      }
    }
  }, [initialSelection?.planId, initialSelection?.period, freePlan?.id]);

  // 8b. Efeito separado para seleção default (roda só uma vez)
  useEffect(() => {
    if (!initialSelection && !selectedOption && premiumPlan) {
      // Pré-selecionar TRIMESTRAL como default (maior economia = maior LTV)
      const defaultPeriod: BillingPeriod = 'quarterly';
      setSelectedOption({ planId: premiumPlan.id, period: defaultPeriod });
    }
  }, [premiumPlan?.id]);

  // 9. Handler de seleção
  const handleSelect = (planId: string, period: BillingPeriod) => {
    const newSelection = { planId, period };
    setSelectedOption(newSelection);
    
    // Se selecionou um plano pago diferente do atual Hero, fazer swap
    if (planId !== heroPlanId && planId !== freePlan?.id) {
      setPreviousPlan(heroPlanId);
      setHeroPlanId(planId);
    }
    
    onSelectionChange(newSelection);
  };

  // 10. Calculate price options para o plano Hero (dinâmico)
  const heroPriceOptions = useMemo(() => {
    if (!heroPlan) return [];
    
    const weeklyPrice = heroPlan.prices.weekly;
    const monthlyPrice = heroPlan.prices.monthly;
    const options: Array<{
      period: BillingPeriod;
      price: number;
      originalPrice?: number;
      savings?: number;
    }> = [];

    // Filtrar apenas períodos com preço > 0
    const allPeriods: BillingPeriod[] = ['monthly', 'quarterly', 'weekly'];
    const periods = allPeriods.filter(period => heroPlan.prices[period] > 0);
    
    periods.forEach((period) => {
      const periodPrice = heroPlan.prices[period];
      
      const calculateOriginalPrice = () => {
        // Se tem preço semanal, usar como base (sponsors)
        if (weeklyPrice > 0) {
          return period === 'monthly' ? weeklyPrice * 4 : 
                 period === 'quarterly' ? weeklyPrice * 12 : 
                 weeklyPrice;
        }
        // Se não tem semanal, calcular economia trimestral baseada no mensal (sponsoreds)
        if (period === 'quarterly' && monthlyPrice > 0) {
          return monthlyPrice * 3;
        }
        return null;
      };
      
      const originalPrice = calculateOriginalPrice();
      const savingsPercent = originalPrice && periodPrice > 0 && originalPrice > periodPrice
        ? Math.round(((originalPrice - periodPrice) / originalPrice) * 100)
        : 0;

      options.push({
        period,
        price: periodPrice,
        originalPrice: originalPrice || undefined,
        savings: savingsPercent > 0 ? savingsPercent : undefined
      });
    });

    return options;
  }, [heroPlan]);

  // 11. Calculate price options para o plano Alternative (dinâmico)
  const alternativePriceOptions = useMemo(() => {
    if (!alternativePlan) return [];
    
    const weeklyPrice = alternativePlan.prices.weekly;
    const monthlyPrice = alternativePlan.prices.monthly;
    const options: Array<{
      period: BillingPeriod;
      price: number;
      originalPrice?: number;
      savings?: number;
    }> = [];

    // Filtrar apenas períodos com preço > 0
    const allPeriods: BillingPeriod[] = ['monthly', 'quarterly', 'weekly'];
    const periods = allPeriods.filter(period => alternativePlan.prices[period] > 0);
    
    periods.forEach((period) => {
      const periodPrice = alternativePlan.prices[period];
      
      const calculateOriginalPrice = () => {
        // Se tem preço semanal, usar como base (sponsors)
        if (weeklyPrice > 0) {
          return period === 'monthly' ? weeklyPrice * 4 : 
                 period === 'quarterly' ? weeklyPrice * 12 : 
                 weeklyPrice;
        }
        // Se não tem semanal, calcular economia trimestral baseada no mensal (sponsoreds)
        if (period === 'quarterly' && monthlyPrice > 0) {
          return monthlyPrice * 3;
        }
        return null;
      };
      
      const originalPrice = calculateOriginalPrice();
      const savingsPercent = originalPrice && periodPrice > 0 && originalPrice > periodPrice
        ? Math.round(((originalPrice - periodPrice) / originalPrice) * 100)
        : 0;

      options.push({
        period,
        price: periodPrice,
        originalPrice: originalPrice || undefined,
        savings: savingsPercent > 0 ? savingsPercent : undefined
      });
    });

    return options;
  }, [alternativePlan]);

  // 12. Variantes de animação
  const heroVariants = {
    initial: (direction: number) => ({
      y: direction === 0 ? 0 : direction === 1 ? 50 : -50,
      opacity: direction === 0 ? 1 : 0,
      scale: direction === 0 ? 1 : 0.95
    }),
    animate: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: { type: "spring" as const, stiffness: 300, damping: 30, duration: 0.3 }
    },
    exit: (direction: number) => ({
      y: direction === 1 ? -50 : 50,
      opacity: 0,
      scale: 0.95,
      transition: { duration: 0.2 }
    })
  };

  const alternativeVariants = {
    initial: (direction: number) => ({
      y: direction === 0 ? 0 : direction === 1 ? -30 : 30,
      opacity: direction === 0 ? 1 : 0,
      scale: direction === 0 ? 1 : 1.02
    }),
    animate: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: { type: "spring" as const, stiffness: 300, damping: 30, duration: 0.3 }
    },
    exit: (direction: number) => ({
      y: direction === 1 ? 30 : -30,
      opacity: 0,
      scale: 0.98,
      transition: { duration: 0.2 }
    })
  };

  // 13. Validação - só precisa de premiumPlan (sponsoreds não têm standardPlan)
  if (!premiumPlan) return null;

  // 14. JSX
  return (
    <div className="space-y-6 transition-all duration-300 ease-in-out">
      {/* Social proof bar - só exibe quando há 50+ usuários pagantes */}
      {(socialProofStats?.paying_users_count ?? 0) >= 50 && (
        <SocialProofBar 
          subscriberCount={socialProofStats.paying_users_count}
          topAvatars={socialProofStats?.top_avatars}
          isLoading={socialProofLoading}
          className="mb-4" 
          densityMode={densityMode} 
        />
      )}

      {/* DESKTOP: Hero + Alternatives side by side */}
      <div className="hidden md:block">
        <div className="grid grid-cols-1 gap-6 mb-6">
          {/* Hero card - Dinâmico com animação */}
          <motion.div
            key={`hero-${heroPlan?.id}`}
            custom={direction}
            variants={heroVariants}
            initial={direction !== 0 ? "initial" : false}
            animate="animate"
            exit="exit"
          >
            <PricingHeroCard
              planId={heroPlan.id}
              planName={heroPlan.name}
              planTier={heroPlanTier}
              topDifferentials={PLAN_DIFFERENTIALS[heroPlan.id]?.topDifferentials || []}
              priceOptions={heroPriceOptions}
              selectedOption={selectedOption}
              onSelectOption={handleSelect}
              isCurrent={currentPlan === heroPlan.id}
              densityMode={densityMode}
            />
          </motion.div>
        </div>

        {/* Alternative plans - flex layout with proportional widths */}
        <div className="flex gap-4">
          {alternativePlan && (
            <motion.div 
              className="flex-[2]"
              key={`alt-${alternativePlan.id}`}
              custom={direction}
              variants={alternativeVariants}
              initial={direction !== 0 ? "initial" : false}
              animate="animate"
              exit="exit"
            >
              <AlternativePlanCard
                planId={alternativePlan.id}
                planName={alternativePlan.name}
                planTier={alternativePlanTier}
                miniSummary={PLAN_DIFFERENTIALS[alternativePlan.id]?.miniSummary || ""}
                price={alternativePlan.prices.monthly}
                isCurrent={currentPlan === alternativePlan.id}
                isFree={false}
                badge={
                  alternativePlanTier === 'premium' 
                    ? { text: "⭐ MAIS POPULAR", className: "bg-luxury-pinkBright/20 text-luxury-pinkBright border-luxury-pinkBright/30" }
                    : { text: "💰 Melhor Custo-Benefício", className: "bg-amber-500/20 text-amber-400 border-amber-500/30" }
                }
                densityMode={densityMode}
                onSelect={handleSelect}
                isSelected={selectedOption?.planId === alternativePlan.id}
                priceOptions={alternativePriceOptions}
                selectedOption={selectedOption}
              />
            </motion.div>
          )}
          
          {freePlan && (
            <div className="flex-1">
              <AlternativePlanCard
                planId={freePlan.id}
                planName={freePlan.name}
                miniSummary="Limitado"
                isCurrent={currentPlan === freePlan.id}
                isFree={true}
                densityMode={densityMode}
              />
            </div>
          )}
        </div>
      </div>

      {/* MOBILE: Scroll natural com padding para CTA */}
      <div className="md:hidden flex flex-col space-y-3 pb-[160px]">
        {/* Hero card */}
        <div>
          <motion.div
            key={`hero-mobile-${heroPlan?.id}`}
            custom={direction}
            variants={heroVariants}
            initial={direction !== 0 ? "initial" : false}
            animate="animate"
            exit="exit"
          >
            <PricingHeroCard
              planId={heroPlan.id}
              planName={heroPlan.name}
              planTier={heroPlanTier}
              topDifferentials={PLAN_DIFFERENTIALS[heroPlan.id]?.topDifferentials || []}
              priceOptions={heroPriceOptions}
              selectedOption={selectedOption}
              onSelectOption={handleSelect}
              isCurrent={currentPlan === heroPlan.id}
              densityMode={densityMode}
            />
          </motion.div>
        </div>

        {/* Alternative plans - accordion em compact, visíveis em normal/expanded */}
        {densityMode === 'compact' ? (
          <Collapsible className="mt-3" open={isOtherPlansOpen} onOpenChange={setIsOtherPlansOpen}>
            <CollapsibleTrigger className="w-full flex items-center justify-center gap-2 text-sm text-white/60 hover:text-white/80 transition-colors py-2">
              <ChevronDown className={cn(
                "w-4 h-4 transition-transform duration-200",
                isOtherPlansOpen && "rotate-180"
              )} />
              <span>Ver outros planos</span>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-2 pt-2">
              {alternativePlan && (
              <div>
                <motion.div
                  key={`alt-mobile-collapsible-${alternativePlan.id}`}
                  custom={direction}
                  variants={alternativeVariants}
                  initial={direction !== 0 ? "initial" : false}
                  animate="animate"
                  exit="exit"
                >
                  <AlternativePlanCard
                    planId={alternativePlan.id}
                    planName={alternativePlan.name}
                    planTier={alternativePlanTier}
                    miniSummary={PLAN_DIFFERENTIALS[alternativePlan.id]?.miniSummary || ""}
                    price={alternativePlan.prices.monthly}
                    isCurrent={currentPlan === alternativePlan.id}
                    isFree={false}
                    badge={
                      alternativePlanTier === 'premium' 
                        ? { text: "⭐ MAIS POPULAR", className: "bg-luxury-pinkBright/20 text-luxury-pinkBright border-luxury-pinkBright/30" }
                        : { text: "💰 Melhor Custo", className: "bg-amber-500/20 text-amber-400 border-amber-500/30" }
                    }
                    densityMode={densityMode}
                    onSelect={handleSelect}
                    isSelected={selectedOption?.planId === alternativePlan.id}
                    priceOptions={alternativePriceOptions}
                    selectedOption={selectedOption}
                  />
                </motion.div>
              </div>
              )}
              
              {freePlan && (
                <div>
                  <AlternativePlanCard
                    planId={freePlan.id}
                    planName={freePlan.name}
                    miniSummary="Limitado"
                    isCurrent={currentPlan === freePlan.id}
                    isFree={true}
                    densityMode={densityMode}
                  />
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        ) : (
          <>
            {alternativePlan && (
              <div>
                <motion.div
                  key={`alt-mobile-${alternativePlan.id}`}
                  custom={direction}
                  variants={alternativeVariants}
                  initial={direction !== 0 ? "initial" : false}
                  animate="animate"
                  exit="exit"
                >
                  <AlternativePlanCard
                    planId={alternativePlan.id}
                    planName={alternativePlan.name}
                    planTier={alternativePlanTier}
                    miniSummary={PLAN_DIFFERENTIALS[alternativePlan.id]?.miniSummary || ""}
                    price={alternativePlan.prices.monthly}
                    isCurrent={currentPlan === alternativePlan.id}
                    isFree={false}
                    badge={
                      alternativePlanTier === 'premium' 
                        ? { text: "⭐ MAIS POPULAR", className: "bg-luxury-pinkBright/20 text-luxury-pinkBright border-luxury-pinkBright/30" }
                        : { text: "💰 Melhor Custo", className: "bg-amber-500/20 text-amber-400 border-amber-500/30" }
                    }
                    densityMode={densityMode}
                    onSelect={handleSelect}
                    isSelected={selectedOption?.planId === alternativePlan.id}
                    priceOptions={alternativePriceOptions}
                    selectedOption={selectedOption}
                  />
                </motion.div>
              </div>
            )}
            
            {freePlan && (
              <div>
                <AlternativePlanCard
                  planId={freePlan.id}
                  planName={freePlan.name}
                  miniSummary="Limitado"
                  isCurrent={currentPlan === freePlan.id}
                  isFree={true}
                  densityMode={densityMode}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};